
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { ConsentCheckbox } from "@/components/legal/ConsentCheckbox";

interface EmailAuthFormProps {
  isSignUp: boolean;
  onSignUpSuccess: (email: string) => void;
}

export const EmailAuthForm = ({ isSignUp, onSignUpSuccess }: EmailAuthFormProps) => {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [consentGiven, setConsentGiven] = useState(false);

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast.error("Email is required");
      return;
    }

    if (isSignUp && !consentGiven) {
      toast.error("Please accept the terms and privacy policy");
      return;
    }
    
    setLoading(true);

    try {
      console.log(`Attempting to ${isSignUp ? 'sign up' : 'sign in'} with email: ${email}`);
      
      if (isSignUp) {
        // Use magic link for sign up
        const { data, error } = await supabase.auth.signInWithOtp({
          email,
          options: {
            emailRedirectTo: `${window.location.origin}/company-registration`
          }
        });

        if (error) throw error;
        
        console.log("Magic link sent:", data);
        toast.success("Check your email for the magic link to complete sign up!");
        onSignUpSuccess(email);
      } else {
        if (!password) {
          // For existing users, offer magic link option
          const { data, error } = await supabase.auth.signInWithOtp({
            email,
            options: {
              emailRedirectTo: `${window.location.origin}/`
            }
          });

          if (error) throw error;
          toast.success("Check your email for the magic link to sign in!");
        } else {
          // Password sign in for existing users
          const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password,
          });

          if (error) throw error;
          
          console.log("Sign in response:", data);
          
          if (data?.user) {
            toast.success("Successfully signed in!");
          } else {
            toast.error("Sign in failed. Please check your credentials.");
          }
        }
      }
    } catch (error: any) {
      console.error(`${isSignUp ? 'Sign up' : 'Sign in'} error:`, error);
      
      // Provide more helpful error messages
      if (error.message.includes("Email already registered")) {
        toast.error("This email is already registered. Please sign in instead.");
      } else if (error.message.includes("Invalid login credentials")) {
        toast.error("Invalid email or password. Please try again.");
      } else {
        toast.error(error.message || "Authentication failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleEmailAuth} className="space-y-4">
      <div>
        <Input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </div>
      {!isSignUp && (
        <div>
          <Input
            type="password"
            placeholder="Password (or leave blank for magic link)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
      )}
      
      {isSignUp && (
        <ConsentCheckbox
          checked={consentGiven}
          onCheckedChange={setConsentGiven}
          context="signup"
        />
      )}
      
      <Button type="submit" className="w-full" disabled={loading}>
        {loading 
          ? "Sending..." 
          : isSignUp 
            ? "Send Magic Link" 
            : password 
              ? "Sign In" 
              : "Send Magic Link"
        }
      </Button>
    </form>
  );
};
